// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.
import React from 'react';

import {FormattedMessage} from 'react-intl';

import Timestamp, {RelativeRanges} from 'components/timestamp';
import {Props as TimestampProps} from 'components/timestamp/timestamp';

import {getCurrentDateTimeForTimezone, isSame, isAfter, isBefore, endOf, add} from 'utils/datetime';

const CUSTOM_STATUS_EXPIRY_RANGES = [
    RelativeRanges.TODAY_TITLE_CASE,
    RelativeRanges.TOMORROW_TITLE_CASE,
];

interface Props {
    time: string;
    timezone?: string;
    className?: string;
    showPrefix?: boolean;
    withinBrackets?: boolean;
}

const ExpiryTime = ({time, timezone, className, showPrefix, withinBrackets}: Props) => {
    const currentDateTime = getCurrentDateTimeForTimezone(timezone);
    const timestampProps: Partial<TimestampProps> = {
        value: time,
        ranges: CUSTOM_STATUS_EXPIRY_RANGES,
    };

    // a isSame as End of today || a isAfter the end of next day
    if (isSame(time, endOf(currentDateTime, 'day')) || isAfter(time, endOf(add(currentDateTime, 1, 'day'), 'day'))) {
        timestampProps.useTime = false;
    }

    // a isBefore the End of today
    if (isBefore(time, endOf(currentDateTime, 'day'))) {
        timestampProps.useDate = false;
        delete timestampProps.ranges;
    }

    // a isAfter the End of next day && a isBefore now+6 days
    if (isAfter(time, endOf(add(currentDateTime, 1, 'day'), 'day')) && isBefore(time, add(currentDateTime, 6, 'days'))) {
        timestampProps.useDate = {weekday: 'long'};
    }

    // a isAfter now+6 days
    if (isAfter(time, add(currentDateTime, 6, 'days'))) {
        timestampProps.month = 'short';
    }

    // a isAfter the End of year
    if (isAfter(time, endOf(currentDateTime, 'year'))) {
        timestampProps.year = 'numeric';
    }

    const prefix = showPrefix && (
        <>
            <FormattedMessage
                id='custom_status.expiry.until'
                defaultMessage='until'
            />{' '}
        </>
    );

    return (
        <span className={className}>
            {withinBrackets && '('}
            {prefix}
            <Timestamp
                {...timestampProps}
            />
            {withinBrackets && ')'}
        </span>
    );
};

ExpiryTime.defaultProps = {
    showPrefix: true,
    withinBrackets: false,
};

export default React.memo(ExpiryTime);
